import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { Field, NativeSelect } from "@/components/kit";
import { LeaveStatus } from "@/components/payroll/Leaves";
import { dmy, errMsg } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/employee-portal`;
const BLANK = { type: "paid", reason: "marriage", start: "", end: "", note: "" };

const RequestForm = ({ h, onDone, onExpired }) => {
  const { t } = useApp();
  const [f, setF] = useState(BLANK);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/leaves`, { ...f, reason: f.type === "exceptional" ? f.reason : null }, h);
      if (file && f.type === "sick") { const fd = new FormData(); fd.append("file", file); await axios.post(`${API}/leaves/${data.id}/attachment`, fd, h); }
      toast.success(t("leave_sent")); setF(BLANK); setFile(null); onDone();
    } catch (x) { if (x.response?.status === 401) onExpired(); else toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border bg-white p-4" data-testid="emp-leave-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("leave_type")}><NativeSelect value={f.type} onChange={set("type")} data-testid="emp-leave-type">{["paid", "sick", "unpaid", "exceptional"].map((x) => <option key={x} value={x}>{t(`leave_t_${x}`)}</option>)}</NativeSelect></Field>
        {f.type === "exceptional" && <Field label={t("leave_reason")}><NativeSelect value={f.reason} onChange={set("reason")} data-testid="emp-leave-reason">{["marriage", "birth", "death", "other"].map((x) => <option key={x} value={x}>{t(`leave_r_${x}`)}</option>)}</NativeSelect></Field>}
        <Field label={t("leave_start")}><Input type="date" value={f.start} onChange={set("start")} required data-testid="emp-leave-start" /></Field>
        <Field label={t("leave_end")}><Input type="date" value={f.end} min={f.start} onChange={set("end")} required data-testid="emp-leave-end" /></Field>
      </div>
      <Field label={t("leave_comment")}><Input value={f.note} onChange={set("note")} maxLength={500} data-testid="emp-leave-note" /></Field>
      {f.type === "sick" && <Field label={t("leave_certificate")}><Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="emp-leave-file" /></Field>}
      <Button type="submit" disabled={busy || !f.start || !f.end} data-testid="emp-leave-submit"><Plus className="h-4 w-4 me-1" />{t("leave_request")}</Button>
    </form>
  );
};

export const PortalLeaves = ({ session, onExpired }) => {
  const { t } = useApp();
  const [d, setD] = useState(null);
  const h = { headers: { "X-Employee-Session": session } };
  const load = useCallback(() => axios.get(`${API}/leaves`, h).then((r) => setD(r.data)).catch((e) => e.response?.status === 401 && onExpired()), [session]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const cancel = async (id) => { if (!window.confirm(t("leave_cancel_confirm"))) return; try { await axios.delete(`${API}/leaves/${id}`, h); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return null;
  const b = d.balance;
  return (
    <section className="space-y-3" data-testid="emp-leaves">
      <h2 className="text-sm font-semibold text-slate-700">{t("emp_portal_leaves")}</h2>
      <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4" data-testid="emp-leave-balance">
        {[["acquired", b.acquired], ["taken", b.taken], ["pending", b.pending], ["available", b.available]].map(([k, v]) => (
          <div key={k} className="rounded-xl border bg-white p-3"><div className="eyebrow">{t(`leave_b_${k}`)}</div><div className="num text-lg font-bold" data-testid={`emp-leave-${k}`}>{v}</div></div>))}
      </div>
      <RequestForm h={h} onDone={load} onExpired={onExpired} />
      {d.requests.length > 0 && <ul className="divide-y rounded-xl border bg-white">{d.requests.map((lv) => (
        <li key={lv.id} className="flex flex-wrap items-center gap-2 px-4 py-3 text-sm" data-testid={`emp-leave-${lv.id}`}>
          <span className="font-medium">{t(`leave_t_${lv.type}`)}{lv.reason && ` · ${t(`leave_r_${lv.reason}`)}`}{lv.attachment_file_id && <Paperclip className="ms-1 inline h-3.5 w-3.5 text-slate-500" />}</span><span className="num text-xs text-slate-500">{dmy(lv.start)} → {dmy(lv.end)} · {lv.days} j</span>
          <span className="ms-auto flex items-center gap-2"><LeaveStatus s={lv.status} t={t} />
            {lv.status === "pending" && <Button size="sm" variant="ghost" onClick={() => cancel(lv.id)} data-testid={`emp-leave-cancel-${lv.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}</span>
          {lv.manager_note && <div className="w-full text-xs text-slate-500">↳ {lv.manager_note}</div>}
        </li>))}</ul>}
    </section>
  );
};
